import React from 'react';
import ExternalCoachAgentSettings from './ExternalCoachAgentSettings';
import { getCoachImageVariantUrl } from '../utils/coachImageVariants';
import './ExternalCoachAgentSettings.css';

export const AI_EMOJI_OPTIONS = ['🤖', '🧠', '💪', '🏋️', '🥗', '🎯', '⚡', '🔥', '🌟', '👨‍🔬', '👩‍🔬', '🦾', '🐉', '🦁', '🐺'];
export const AI_INTENSITY_LEVELS = [
  { id: 'gentle', label: 'Gentle', description: 'Light reminders, very patient' },
  { id: 'balanced', label: 'Balanced', description: 'Regular check-ins, moderate push' },
  { id: 'intense', label: 'Intense', description: 'Frequent nudges, accountability focus' },
  { id: 'relentless', label: 'Relentless', description: 'Maximum pressure, no mercy' },
  { id: 'custom', label: 'Custom', description: 'Define your own intensity level' }
];
export const AI_COACH_PERSONALITY_PRESETS = [
  { id: 'kata_default', label: 'Kata.fit default', description: 'Knowledgeable, practical, and focused on sustainable progress' },
  { id: 'supportive_mentor', label: 'Supportive mentor', description: 'Patient, empathetic, confidence-building, and encouraging' },
  { id: 'demanding_coach', label: 'Demanding coach', description: 'High standards, direct accountability, and constructive challenge' },
  { id: 'science_educator', label: 'Science educator', description: 'Analytical, curious, and explains the reasoning behind advice' },
  { id: 'casual_buddy', label: 'Casual workout buddy', description: 'Relaxed, friendly, conversational, and fun' },
  { id: 'custom', label: 'Custom coach', description: 'Define the biography, motivation, values, voice, and behavior' }
];
export const AI_PERSONALITY_PRESETS = AI_COACH_PERSONALITY_PRESETS;

export { ExternalCoachAgentSettings };

const AiCoachSettings = ({
  name = '', setName = () => {}, emoji, imageUrl, imagePreviewUrl = null, setEmoji, setImageUrl, setImageFile,
  personalityPreset = 'kata_default', personalityCustom = '',
  setPersonalityPreset = () => {}, setPersonalityCustom = () => {},
  intensity, intensityCustom, setIntensity, setIntensityCustom,
  readOnly = false, establishedBy = null, onViewEstablished,
  personal = false, backendService = null
}) => {
  const displayImageUrl = getCoachImageVariantUrl({ image_url: imageUrl, image_preview_url: imagePreviewUrl }, 'preview');

  return <div className={`ai-coach-settings-shared ${readOnly ? 'ai-coach-settings-readonly' : ''}`}>
    {readOnly && (
      <p className="ai-settings-established-notice">
        🔒 Established by {establishedBy || 'your dojo'} — members share these AI coach settings.{' '}
        {onViewEstablished && <button type="button" className="ai-settings-link" onClick={onViewEstablished}>View dojo settings</button>}
      </p>
    )}
    <div className="form-group">
      <label className="user-profile-label" htmlFor="ai-coach-name">Coach name</label>
      <input id="ai-coach-name" className="user-profile-input" disabled={readOnly} value={name} onChange={event => setName(event.target.value)} maxLength={80} placeholder="e.g. Atlas" />
    </div>
    <div className="form-group">
      <label className="user-profile-label">Assistant Appearance</label>
      <div className="ai-coach-appearance">
        <div className="ai-coach-preview">
          {imageUrl ? <img src={displayImageUrl} alt="AI Assistant" className="ai-coach-image" /> : <span className="ai-coach-emoji-preview">{emoji}</span>}
        </div>
        <div className="ai-coach-emoji-grid">
          {AI_EMOJI_OPTIONS.map(option => <button key={option} type="button" disabled={readOnly} className={`ai-coach-emoji-btn ${emoji === option && !imageUrl ? 'selected' : ''}`} onClick={() => { setEmoji(option); setImageUrl(null); setImageFile(null); }}>{option}</button>)}
        </div>
        <div className="ai-coach-image-upload">
          <input type="file" accept="image/*" disabled={readOnly} style={{ display: 'none' }} id="shared-ai-coach-image" onChange={(event) => { const file = event.target.files?.[0]; if (file) { setImageFile(file); setImageUrl(URL.createObjectURL(file)); } }} />
          <button type="button" className="btn-select" disabled={readOnly} onClick={() => document.getElementById('shared-ai-coach-image')?.click()}>📷 Use Custom Image</button>
          {imageUrl && <button type="button" className="btn-cancel" disabled={readOnly} onClick={() => { setImageUrl(null); setImageFile(null); }}>Clear</button>}
        </div>
      </div>
    </div>
    <div className="form-group ai-coach-personality-group">
      <label className="user-profile-label">Coach personality and communication</label>
      <p className="ai-coach-personality-help">One setting controls who your coach is and how they communicate: biography, motivation, values, voice, and behavior.</p>
      <div className="ai-coach-tone-grid">
        {AI_COACH_PERSONALITY_PRESETS.map(option => <button key={option.id} type="button" disabled={readOnly} className={`ai-coach-tone-btn ${personalityPreset === option.id ? 'selected' : ''}`} onClick={() => setPersonalityPreset(option.id)}><span className="tone-label">{option.label}</span><span className="tone-desc">{option.description}</span></button>)}
      </div>
      {personalityPreset === 'custom' && <textarea className="user-profile-input ai-coach-personality-input" disabled={readOnly} value={personalityCustom} onChange={event => setPersonalityCustom(event.target.value)} maxLength={4000} placeholder="Describe your coach’s biography, motivation, values, voice, communication style, and behavior. Avoid secrets or sensitive personal data." rows={7} />}
    </div>
    <div className="form-group">
      <label className="user-profile-label">Push Intensity</label>
      <div className="ai-coach-intensity-slider">
        {AI_INTENSITY_LEVELS.map(option => <button key={option.id} type="button" disabled={readOnly} className={`ai-coach-intensity-btn ${intensity === option.id ? 'selected' : ''}`} onClick={() => setIntensity(option.id)}><span className="intensity-label">{option.label}</span></button>)}
      </div>
      <p className="intensity-description">{AI_INTENSITY_LEVELS.find(option => option.id === intensity)?.description}</p>
      {intensity === 'custom' && <textarea className="user-profile-input" disabled={readOnly} value={intensityCustom} onChange={event => setIntensityCustom(event.target.value)} placeholder="Describe your preferred intensity level..." rows={2} />}
    </div>
    {personal && !readOnly && backendService && <ExternalCoachAgentSettings backendService={backendService} />}
  </div>;
};

export default AiCoachSettings;
